// GoldSrc BSP v30 parser (Half-Life / Counter-Strike 1.6 maps).
// Pure data extraction — no three.js — so it can be unit-tested under Node.

import { decodeMipTex, type DecodedTexture } from './wad'

export interface BspFace {
  firstEdge: number
  numEdges: number
  texInfo: number
  styles: [number, number, number, number]
  lightOffset: number
}

export interface BspTexInfo {
  /** s axis (vec3) + offset → texture U = dot(pos, s.xyz) + s.w */
  s: [number, number, number, number]
  /** t axis (vec3) + offset → texture V */
  t: [number, number, number, number]
  miptex: number
  flags: number
}

export interface BspTexture {
  name: string
  width: number
  height: number
  /** Decoded pixels when the texture is embedded; null when it lives in a WAD. */
  embedded: DecodedTexture | null
}

export interface BspModel {
  firstFace: number
  numFaces: number
  origin: [number, number, number]
}

export type Entity = Record<string, string>

export interface Bsp {
  vertices: Float32Array // [x, y, z] * n, GoldSrc coordinates
  edges: Uint16Array // [v0, v1] * n
  surfEdges: Int32Array // signed index into edges
  faces: BspFace[]
  texInfos: BspTexInfo[]
  textures: BspTexture[]
  lighting: Uint8Array // RGB luxels
  models: BspModel[]
  entities: Entity[]
  /** WAD basenames referenced by worldspawn, e.g. ["halflife.wad", "cs_dust.wad"]. */
  wads: string[]
}

const HEADER_SIZE = 4

function lump(dv: DataView, index: number): { off: number; len: number } {
  const o = HEADER_SIZE + index * 8
  return { off: dv.getInt32(o, true), len: dv.getInt32(o + 4, true) }
}

function readName(dv: DataView, base: number, max: number): string {
  let name = ''
  for (let i = 0; i < max; i++) {
    const ch = dv.getUint8(base + i)
    if (ch === 0) break
    name += String.fromCharCode(ch)
  }
  return name
}

/** Parse the flat `{ "key" "value" ... }` entity text into dictionaries. */
export function parseEntities(text: string): Entity[] {
  const entities: Entity[] = []
  const block = /\{([^}]*)\}/g
  const pair = /"([^"]*)"\s*"([^"]*)"/g
  let b: RegExpExecArray | null
  while ((b = block.exec(text))) {
    const entity: Entity = {}
    let p: RegExpExecArray | null
    while ((p = pair.exec(b[1]))) entity[p[1]] = p[2]
    entities.push(entity)
  }
  return entities
}

export function parseBsp(buffer: ArrayBuffer): Bsp {
  const dv = new DataView(buffer)
  const version = dv.getInt32(0, true)
  if (version !== 30) {
    throw new Error(
      `Unsupported BSP version ${version} (expected 30 / GoldSrc)`,
    )
  }

  const L = {
    entities: lump(dv, 0),
    textures: lump(dv, 2),
    vertexes: lump(dv, 3),
    texinfo: lump(dv, 6),
    faces: lump(dv, 7),
    lighting: lump(dv, 8),
    edges: lump(dv, 12),
    surfedges: lump(dv, 13),
    models: lump(dv, 14),
  }

  // Bulk lumps copied into aligned typed arrays.
  const slice = (off: number, len: number) => buffer.slice(off, off + len)
  const vertices = new Float32Array(slice(L.vertexes.off, L.vertexes.len))
  const edges = new Uint16Array(slice(L.edges.off, L.edges.len))
  const surfEdges = new Int32Array(slice(L.surfedges.off, L.surfedges.len))
  const lighting = new Uint8Array(slice(L.lighting.off, L.lighting.len))

  // Faces (20 bytes each).
  const faceCount = L.faces.len / 20
  const faces: BspFace[] = new Array(faceCount)
  for (let i = 0; i < faceCount; i++) {
    const o = L.faces.off + i * 20
    faces[i] = {
      firstEdge: dv.getInt32(o + 4, true),
      numEdges: dv.getUint16(o + 8, true),
      texInfo: dv.getUint16(o + 10, true),
      styles: [
        dv.getUint8(o + 12),
        dv.getUint8(o + 13),
        dv.getUint8(o + 14),
        dv.getUint8(o + 15),
      ],
      lightOffset: dv.getInt32(o + 16, true),
    }
  }

  // TexInfo (40 bytes each).
  const texInfoCount = L.texinfo.len / 40
  const texInfos: BspTexInfo[] = new Array(texInfoCount)
  const f = (x: number) => dv.getFloat32(x, true)
  for (let i = 0; i < texInfoCount; i++) {
    const o = L.texinfo.off + i * 40
    texInfos[i] = {
      s: [f(o), f(o + 4), f(o + 8), f(o + 12)],
      t: [f(o + 16), f(o + 20), f(o + 24), f(o + 28)],
      miptex: dv.getUint32(o + 32, true),
      flags: dv.getUint32(o + 36, true),
    }
  }

  // Textures lump: count + relative offsets + miptex headers.
  const tBase = L.textures.off
  const numTextures = dv.getInt32(tBase, true)
  const textures: BspTexture[] = new Array(numTextures)
  for (let i = 0; i < numTextures; i++) {
    const rel = dv.getInt32(tBase + 4 + i * 4, true)
    if (rel < 0) {
      textures[i] = { name: '', width: 0, height: 0, embedded: null }
      continue
    }
    const base = tBase + rel
    const mip0 = dv.getUint32(base + 24, true)
    textures[i] = {
      name: readName(dv, base, 16),
      width: dv.getUint32(base + 16, true),
      height: dv.getUint32(base + 20, true),
      embedded: mip0 !== 0 ? decodeMipTex(dv, base) : null,
    }
  }

  // Models (64 bytes each) — only origin + face range are needed.
  const modelCount = L.models.len / 64
  const models: BspModel[] = new Array(modelCount)
  for (let i = 0; i < modelCount; i++) {
    const o = L.models.off + i * 64
    models[i] = {
      origin: [f(o + 24), f(o + 28), f(o + 32)],
      firstFace: dv.getInt32(o + 56, true),
      numFaces: dv.getInt32(o + 60, true),
    }
  }

  // Entities.
  const entText = readAscii(buffer, L.entities.off, L.entities.len)
  const entities = parseEntities(entText)
  const worldspawn = entities[0] ?? {}
  const wads = (worldspawn.wad ?? '')
    .split(';')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => p.split(/[\\/]/).pop()!.toLowerCase())

  return {
    vertices,
    edges,
    surfEdges,
    faces,
    texInfos,
    textures,
    lighting,
    models,
    entities,
    wads,
  }
}

function readAscii(buffer: ArrayBuffer, off: number, len: number): string {
  const bytes = new Uint8Array(buffer, off, len)
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return s
}
